/**
 * The shared "add evidence" dialog chrome. It owns the modal, the header, a
 * classifier slot (the caller decides what the file is - a document category,
 * an inspection kind + caption, etc.), the shared FileUploader with its upload
 * orchestration, and a Done footer. It is deliberately unaware of how files
 * are classified or where they're stored: the caller passes a `classifier`
 * node, a `classifierReady` gate, and an `uploadFn` closure that already
 * captures the chosen classifier values.
 *
 * Both the claim Evidence card and the inspection Evidence tab render this, so
 * the upload UX stays identical across surfaces while each keeps its own
 * classifier and transport.
 */
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import {
  FileUploader,
  type DuplicateCheck,
  type UploadAttempt,
  type UploadResult,
} from '@/shared/components/ui/file-uploader'
import type { UploadedFileItem } from '@/shared/lib/file-types'
import { cn } from '@/shared/lib/utils'

export function AddEvidenceDialogShell({
  title = 'Add evidence',
  description,
  banner,
  classifier,
  classifierReady = true,
  loading = false,
  error,
  value,
  onChange,
  uploadFn,
  retryLostUploads = false,
  duplicateCheck,
  onSkip,
  accept,
  acceptLabel,
  multiple = true,
  canRemove = false,
  onRemove,
  readyHint = 'Drop or click to upload',
  notReadyHint,
  filesLabel = 'Files',
  footerNote,
  doneLabel = 'Done',
  contentClassName,
  onClose,
}: {
  title?: string
  description?: string
  /** Optional banner above the classifier (e.g. a "still needed" checklist). */
  banner?: ReactNode
  /** Classifier fields - what the file is. Rendered above the uploader. */
  classifier?: ReactNode
  /** Gate: the uploader stays disabled until the classifier is complete. */
  classifierReady?: boolean
  /** Show a spinner instead of the body (e.g. classifier config loading). */
  loading?: boolean
  /** Error copy shown instead of the body when the classifier config failed. */
  error?: string | null
  value: UploadedFileItem[]
  onChange: (files: UploadedFileItem[]) => void
  uploadFn: (file: File, attempt: UploadAttempt) => Promise<UploadResult>
  /** See `FileUploader.retryLostUploads`. */
  retryLostUploads?: boolean
  /** See `FileUploader.duplicateCheck`. */
  duplicateCheck?: DuplicateCheck
  onSkip?: (file: File, reason: 'repeated' | 'existing', delta: 1 | -1) => void
  accept: string
  acceptLabel?: string
  multiple?: boolean
  /** Show the per-row remove control on committed files. */
  canRemove?: boolean
  /** Delegated remove handler (caller owns the confirm + server delete). */
  onRemove?: (file: UploadedFileItem, index: number) => void
  readyHint?: string
  /** Hint shown while the classifier is incomplete (falls back to readyHint). */
  notReadyHint?: string
  filesLabel?: string
  /** Small print under the uploader (e.g. capture-timestamp note). */
  footerNote?: ReactNode
  doneLabel?: string
  contentClassName?: string
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [confirmClose, setConfirmClose] = useState(false)
  // Closing unmounts the uploader, and with it every file not yet sent - so
  // while a batch is still going, ask first.
  const requestClose = () => (busy ? setConfirmClose(true) : onClose())

  return (
    <>
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) requestClose()
      }}
    >
      <DialogContent className={cn('max-w-[560px]', contentClassName)}>
        <DialogHeader>
          <div className="flex flex-col gap-1">
            <DialogTitle className="text-[20px]">{title}</DialogTitle>
            {description ? (
              <DialogDescription className="text-[14px] text-[var(--ink-3)]">
                {description}
              </DialogDescription>
            ) : null}
          </div>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-[15px] text-[var(--ink-3)]">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Loading…
            </div>
          ) : error ? (
            <p className="py-12 text-center text-[15px] text-[var(--status-error)]">{error}</p>
          ) : (
            <>
              {banner}
              {classifier}

              <div className="flex flex-col gap-2">
                <Label className="text-[12px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                  {filesLabel}
                </Label>
                <FileUploader
                  value={value}
                  onChange={onChange}
                  uploadFn={uploadFn}
                  retryLostUploads={retryLostUploads}
                  duplicateCheck={duplicateCheck}
                  onSkip={onSkip}
                  onBusyChange={setBusy}
                  multiple={multiple}
                  accept={accept}
                  acceptLabel={acceptLabel}
                  disabled={!classifierReady}
                  canRemove={canRemove}
                  hint={classifierReady ? readyHint : (notReadyHint ?? readyHint)}
                  onRemove={onRemove}
                />
                {footerNote ? (
                  <p className="text-[11px] text-[var(--ink-3)]">{footerNote}</p>
                ) : null}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button onClick={requestClose} variant="outline">
            {doneLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={confirmClose} onOpenChange={setConfirmClose}>
      <DialogContent className="max-w-[420px]">
        <DialogHeader>
          <DialogTitle>
            {t('evidence.stillUploadingTitle', 'Files are still uploading')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'evidence.stillUploadingBody',
              'If you close this window now, the files that have not uploaded yet will not be added.',
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('evidence.closeAnyway', 'Close anyway')}
          </Button>
          <Button onClick={() => setConfirmClose(false)}>
            {t('evidence.keepUploading', 'Keep uploading')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  )
}
